"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useRouter } from "next/navigation"
import {
  retrieveCartSnapshot,
  setCartLineQuantity,
} from "@lib/data/cart"
import { useGlobalAside } from "@modules/global-shell/context/global-aside-context"
import { buildCartDrawerViewModel } from "../view-model"
import {
  CartMutationError,
  isCartConflict,
  isCartReplacedByRecovery,
  isCartUnavailable,
  type CartDrawerLine,
  type CartDrawerViewModel,
  type CartPendingAddPreview,
  type CartSnapshot,
} from "../types"

export type CartNoticeKind = "mutation-error" | "stale"

export type CartPendingAction = {
  lineKey: string
  action: "decrease" | "select" | "increase" | "remove"
}

export type CartDrawerActions = {
  retrieve(): Promise<CartSnapshot>
  setQuantity(input: {
    lineId: string
    quantity: number
  }): Promise<CartSnapshot>
}

export type CartDrawerContextValue = {
  isOpen: boolean
  snapshot: CartSnapshot
  model: CartDrawerViewModel
  isRefreshing: boolean
  isAdding: boolean
  pendingAdd: CartPendingAddPreview | null
  pending: CartPendingAction | null
  error: string | null
  noticeKind: CartNoticeKind | null
  noticeLineKey: string | null
  openCart(): void
  closeCart(): void
  beginAdd(preview: CartPendingAddPreview): void
  endAdd(): void
  replaceSnapshot(snapshot: CartSnapshot): void
  refreshSnapshot(): Promise<void>
  setLineQuantity(
    line: CartDrawerLine,
    quantity: number,
    action: CartPendingAction["action"]
  ): Promise<void>
  restoreOpenerFocus(): void
}

const realActions: CartDrawerActions = {
  retrieve: retrieveCartSnapshot,
  setQuantity: async (input) => {
    const result = await setCartLineQuantity(input)
    if (!result.ok) {
      throw new CartMutationError(result.message, result.status, result.reason)
    }
    return result.snapshot
  },
}

const CartDrawerContext = createContext<CartDrawerContextValue | null>(null)

export function CartDrawerProvider({
  initialSnapshot,
  actions = realActions,
  children,
}: {
  initialSnapshot: CartSnapshot
  actions?: CartDrawerActions
  children: ReactNode
}) {
  const router = useRouter()
  const globalAside = useGlobalAside()
  const [snapshot, setSnapshot] = useState(initialSnapshot)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [pendingAdd, setPendingAdd] = useState<CartPendingAddPreview | null>(null)
  const [pending, setPending] = useState<CartPendingAction | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [noticeKind, setNoticeKind] = useState<CartNoticeKind | null>(null)
  const [noticeLineKey, setNoticeLineKey] = useState<string | null>(null)
  const pendingRef = useRef(false)
  const displayOrderRef = useRef<string[]>([])
  const isOpen = globalAside.isOpen("cart")

  useEffect(() => {
    if (!pendingRef.current) setSnapshot(initialSnapshot)
  }, [initialSnapshot])

  const model = useMemo(() => {
    const nextModel = buildCartDrawerViewModel(snapshot)
    const currentKeys = new Set(nextModel.lines.map((line) => line.key))
    const stableOrder = displayOrderRef.current.filter((key) => currentKeys.has(key))

    for (const line of nextModel.lines) {
      if (!stableOrder.includes(line.key)) stableOrder.push(line.key)
    }

    displayOrderRef.current = stableOrder
    const position = new Map(stableOrder.map((key, index) => [key, index]))

    return {
      ...nextModel,
      lines: [...nextModel.lines].sort(
        (a, b) => (position.get(a.key) ?? Number.MAX_SAFE_INTEGER) - (position.get(b.key) ?? Number.MAX_SAFE_INTEGER)
      ),
    }
  }, [snapshot])

  const restoreOpenerFocus = useCallback(() => {
    globalAside.restoreFocus("cart")
  }, [globalAside])

  const openCart = useCallback(() => {
    globalAside.open("cart")
  }, [globalAside])

  const closeCart = useCallback(() => {
    globalAside.close("cart")
  }, [globalAside])

  const beginAdd = useCallback((preview: CartPendingAddPreview) => {
    setPendingAdd(preview)
    setIsAdding(true)
    setError(null)
    setNoticeKind(null)
    setNoticeLineKey(null)
  }, [])

  const endAdd = useCallback(() => {
    setPendingAdd(null)
    setIsAdding(false)
  }, [])

  const replaceSnapshot = useCallback((next: CartSnapshot) => {
    setSnapshot(next)
    setError(null)
    setNoticeKind(null)
    setNoticeLineKey(null)
  }, [])

  const refreshSnapshot = useCallback(async () => {
    if (pendingRef.current) return
    setIsRefreshing(true)
    setError(null)
    setNoticeKind(null)
    setNoticeLineKey(null)
    try {
      const next = await actions.retrieve()
      if (next.loadError) {
        setSnapshot((current) => current.cart
          ? { ...current, loadError: next.loadError }
          : next
        )
      } else {
        setSnapshot(next)
      }
    } catch {
      setSnapshot((current) => ({
        ...current,
        loadError: "Sepet yüklenemedi. Lütfen tekrar deneyin.",
      }))
    } finally {
      setIsRefreshing(false)
    }
  }, [actions])

  const setLineQuantity = useCallback(async (
    line: CartDrawerLine,
    quantity: number,
    action: CartPendingAction["action"]
  ) => {
    if (pendingRef.current) return
    pendingRef.current = true
    setPending({ lineKey: line.key, action })
    setError(null)
    setNoticeKind(null)
    setNoticeLineKey(null)

    try {
      const next = await actions.setQuantity({
        lineId: line.lineId,
        quantity,
      })
      setSnapshot(next)
    } catch (mutationError) {
      if (isCartReplacedByRecovery(mutationError)) {
        try {
          const next = await actions.retrieve()
          if (next.loadError) {
            setSnapshot((current) => ({ ...current, loadError: next.loadError }))
          } else {
            setSnapshot(next)
          }
          router.refresh()
        } finally {
          setError("Bu sepet başka bir oturumda kurtarıldı. Bu sekmedeki eski sepet bağlantısı kaldırıldı.")
          setNoticeKind("stale")
          setNoticeLineKey(null)
        }
      } else if (isCartUnavailable(mutationError)) {
        try {
          const next = await actions.retrieve()
          if (next.loadError) {
            setSnapshot((current) => ({ ...current, loadError: next.loadError }))
          } else {
            setSnapshot(next)
          }
          router.refresh()
        } finally {
          setError("Bu sepet artık kullanılamıyor. Bu sekmedeki eski sepet bağlantısı kaldırıldı.")
          setNoticeKind("stale")
          setNoticeLineKey(null)
        }
      } else if (isCartConflict(mutationError)) {
        try {
          const next = await actions.retrieve()
          if (next.loadError) {
            setSnapshot((current) => ({ ...current, loadError: next.loadError }))
          } else {
            setSnapshot(next)
          }
        } finally {
          setError("Sepetin başka bir işlem nedeniyle değişti. Güncel hali yüklendi.")
          setNoticeKind("stale")
          setNoticeLineKey(null)
        }
      } else {
        setError(
          action === "remove"
            ? "Ürün sepetten kaldırılamadı. Tekrar deneyin."
            : "Adet güncellenemedi. Tekrar deneyin."
        )
        setNoticeKind("mutation-error")
        setNoticeLineKey(line.key)
      }
    } finally {
      pendingRef.current = false
      setPending(null)
    }
  }, [actions, router])

  const value = useMemo<CartDrawerContextValue>(() => ({
    isOpen,
    snapshot,
    model,
    isRefreshing,
    isAdding,
    pendingAdd,
    pending,
    error,
    noticeKind,
    noticeLineKey,
    openCart,
    closeCart,
    beginAdd,
    endAdd,
    replaceSnapshot,
    refreshSnapshot,
    setLineQuantity,
    restoreOpenerFocus,
  }), [
    isOpen,
    snapshot,
    model,
    isRefreshing,
    isAdding,
    pendingAdd,
    pending,
    error,
    noticeKind,
    noticeLineKey,
    openCart,
    closeCart,
    beginAdd,
    endAdd,
    replaceSnapshot,
    refreshSnapshot,
    setLineQuantity,
    restoreOpenerFocus,
  ])

  return (
    <CartDrawerContext.Provider value={value}>
      {children}
    </CartDrawerContext.Provider>
  )
}

export function useCartDrawer() {
  const value = useContext(CartDrawerContext)
  if (!value) throw new Error("useCartDrawer must be used within CartDrawerProvider")
  return value
}
